/**
 * Exposes only actions applicable to the selected Vault item and current role.
 *
 * Menu visibility improves clarity but is never the authorization boundary;
 * backend services revalidate every action. Centralizing the menu keeps list
 * and grid layouts from drifting into different capabilities.
 */
import {
  Copy,
  Download,
  FileUp,
  FolderInput,
  History,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  UserPlus,
} from 'lucide-react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import type { ItemResponse } from '../types'
import { canEdit } from '../lib/format'

interface VaultItemMenuProps {
  entry: ItemResponse
  trashView: boolean
  onDownload: () => void
  onShare: () => void
  onRename: () => void
  onMove: () => void
  onCopy: () => void
  onNewVersion: () => void
  onVersionHistory: () => void
  onTrash: () => void
  onRestore: () => void
}

export function VaultItemMenu({
  entry,
  trashView,
  onDownload,
  onShare,
  onRename,
  onMove,
  onCopy,
  onNewVersion,
  onVersionHistory,
  onTrash,
  onRestore,
}: VaultItemMenuProps) {
  const { item, effectiveRole } = entry
  const editable = canEdit(entry)
  if (!(trashView ? effectiveRole === 'owner' : effectiveRole !== 'none')) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90 data-[state=open]:bg-muted" aria-label={`Actions for ${item.name}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
          {item.name}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {trashView ? (
          <DropdownMenuItem onClick={onRestore} className="gap-2">
            <RotateCcw className="h-4 w-4 text-muted-foreground" /> Restore
          </DropdownMenuItem>
        ) : item.readOnly || !editable ? (
          <>
            {item.kind === 'file' && (
              <>
                <DropdownMenuItem onClick={onDownload} className="gap-2">
                  <Download className="h-4 w-4 text-muted-foreground" /> Download
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onVersionHistory} className="gap-2">
                  <History className="h-4 w-4 text-muted-foreground" /> Version history
                </DropdownMenuItem>
              </>
            )}
            {effectiveRole === 'owner' && (
              <DropdownMenuItem onClick={onShare} className="gap-2">
                <UserPlus className="h-4 w-4 text-muted-foreground" /> Share
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onCopy} className="gap-2">
              <Copy className="h-4 w-4 text-muted-foreground" /> Copy to folder
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {item.kind === 'file' && (
              <>
                <DropdownMenuItem onClick={onDownload} className="gap-2">
                  <Download className="h-4 w-4 text-muted-foreground" /> Download
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onVersionHistory} className="gap-2">
                  <History className="h-4 w-4 text-muted-foreground" /> Version history
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onNewVersion} className="gap-2">
                  <FileUp className="h-4 w-4 text-muted-foreground" /> Upload new version
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {effectiveRole === 'owner' && (
              <DropdownMenuItem onClick={onShare} className="gap-2">
                <UserPlus className="h-4 w-4 text-muted-foreground" /> Share
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onRename} className="gap-2">
              <Pencil className="h-4 w-4 text-muted-foreground" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMove} className="gap-2">
              <FolderInput className="h-4 w-4 text-muted-foreground" /> Move to folder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCopy} className="gap-2">
              <Copy className="h-4 w-4 text-muted-foreground" /> Copy to folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onTrash} className="gap-2 text-destructive focus:bg-destructive/10 focus:text-destructive">
              <Trash2 className="h-4 w-4" /> Move to trash
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
